import { KanbanSquare, List as ListIcon } from "lucide-react";
import { useGetIdentity, useStore, useTranslate } from "ra-core";
import { CreateButton } from "@/components/admin/create-button";
import { DataTable } from "@/components/admin/data-table";
import { List } from "@/components/admin/list";
import { MobileRecordCard } from "@/components/admin/mobile-record-card";
import { ReferenceField } from "@/components/admin/reference-field";
import { SearchInput } from "@/components/admin/search-input";
import { SelectInput } from "@/components/admin/select-input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";

import { TopToolbar } from "../../atomic-crm/layout/TopToolbar";
import {
  findChoice,
  PRIORITIES,
  TEAM_TASK_STATUSES,
} from "../common/constants";
import {
  isOverdue,
  SaleAvatar,
  saleName,
  StatusBadge,
  useFormatDate,
  useSales,
} from "../common/components";
import type { TeamTask } from "../types";
import { TeamTaskKanban } from "./TeamTaskKanban";

type View = "kanban" | "list";

export const TeamTaskList = () => {
  const [view, setView] = useStore<View>("team_tasks.view", "kanban");
  const { data: sales } = useSales();
  const { identity } = useGetIdentity();
  const translate = useTranslate();

  const filters = [
    <SearchInput key="q" source="title@ilike" alwaysOn />,
    <SelectInput
      key="assignee"
      source="assignee_id"
      alwaysOn
      emptyText="hub.common.all"
      choices={(sales ?? []).map((sale) => ({
        id: sale.id,
        name:
          sale.id === identity?.id
            ? `${saleName(sale)} (${translate("hub.common.me")})`
            : saleName(sale),
      }))}
      translateChoice={false}
    />,
    ...(view === "list"
      ? [
          <SelectInput
            key="status"
            source="status"
            alwaysOn
            emptyText="hub.common.all"
            choices={TEAM_TASK_STATUSES}
          />,
        ]
      : []),
  ];

  return (
    <List
      resource="team_tasks"
      title={translate("resources.team_tasks.name", { smart_count: 2 })}
      filters={filters}
      perPage={view === "kanban" ? 500 : 50}
      pagination={view === "kanban" ? false : undefined}
      sort={
        view === "kanban"
          ? { field: "index", order: "ASC" }
          : { field: "due_date", order: "ASC" }
      }
      actions={
        <TopToolbar>
          <ToggleGroup
            value={[view]}
            onValueChange={(value: string[]) =>
              value[0] && setView(value[0] as View)
            }
            variant="outline"
            size="sm"
          >
            <ToggleGroupItem
              value="kanban"
              aria-label={translate("hub.common.kanban")}
            >
              <KanbanSquare className="size-4" />
              {translate("hub.common.kanban")}
            </ToggleGroupItem>
            <ToggleGroupItem
              value="list"
              aria-label={translate("hub.common.list")}
            >
              <ListIcon className="size-4" />
              {translate("hub.common.list")}
            </ToggleGroupItem>
          </ToggleGroup>
          <CreateButton label="hub.team_tasks.new" />
        </TopToolbar>
      }
    >
      {view === "kanban" ? <TeamTaskKanban /> : <TeamTaskTable />}
    </List>
  );
};

const TeamTaskTable = () => {
  const formatDate = useFormatDate();
  return (
    <DataTable<TeamTask>
      rowClick="edit"
      bulkActionButtons={false}
      mobileCard={(task) => (
        <MobileRecordCard
          leading={<SaleAvatar saleId={task.assignee_id} className="size-8" />}
          title={task.title}
          trailing={
            <StatusBadge choice={findChoice(TEAM_TASK_STATUSES, task.status)} />
          }
          subtitle={
            task.deal_id != null ? (
              <ReferenceField source="deal_id" reference="deals" link={false} />
            ) : null
          }
          meta={[
            <StatusBadge
              choice={findChoice(PRIORITIES, task.priority)}
              className="px-2 py-0.5"
            />,
            task.due_date ? (
              <span
                className={cn(
                  isOverdue(task.due_date, task.status === "done") &&
                    "font-medium text-red-600",
                )}
              >
                {formatDate(task.due_date)}
              </span>
            ) : null,
          ]}
        />
      )}
    >
      <DataTable.Col source="title" />
      <DataTable.Col
        source="status"
        render={(task) => (
          <StatusBadge choice={findChoice(TEAM_TASK_STATUSES, task.status)} />
        )}
      />
      <DataTable.Col
        source="priority"
        render={(task) => (
          <StatusBadge choice={findChoice(PRIORITIES, task.priority)} />
        )}
      />
      <DataTable.Col
        source="assignee_id"
        render={(task) => <SaleAvatar saleId={task.assignee_id} />}
      />
      <DataTable.Col
        source="due_date"
        render={(task) => (
          <span
            className={cn(
              isOverdue(task.due_date, task.status === "done") &&
                "font-medium text-red-600",
            )}
          >
            {formatDate(task.due_date)}
          </span>
        )}
      />
      <DataTable.Col source="deal_id">
        <ReferenceField source="deal_id" reference="deals" link="show" />
      </DataTable.Col>
    </DataTable>
  );
};
